(() => {
  'use strict';

  if (window.innerWidth > 767) return;

  const MOBILE_MQ = window.matchMedia('(max-width: 767px)');

  const SELECTORS = {
    section: '.carousel-reviews-section',
    slider: '.carousel-reviews__slider',
    item: '.review-item',
    dot: '.carousel-reviews__dot'
  };

  const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

  function setActiveDot(dots, index) {
    dots.forEach((d, i) => {
      const isActive = i === index;
      d.classList.toggle('carousel-reviews__dot--active', isActive);
      d.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  function getClosestIndexToViewportCenter(sliderEl, items) {
    const sliderRect = sliderEl.getBoundingClientRect();
    const viewportCenterX = sliderRect.left + sliderRect.width / 2;

    let bestIdx = 0;
    let bestDist = Infinity;

    for (let i = 0; i < items.length; i++) {
      const r = items[i].getBoundingClientRect();
      const itemCenterX = r.left + r.width / 2;
      const dist = Math.abs(itemCenterX - viewportCenterX);

      if (dist < bestDist) {
        bestDist = dist;
        bestIdx = i;
      }
    }

    return bestIdx;
  }

  function centerItemInSlider(sliderEl, itemEl) {
    const sliderRect = sliderEl.getBoundingClientRect();
    const itemRect = itemEl.getBoundingClientRect();

    const itemCenterWithinSliderScroll =
      (itemRect.left - sliderRect.left) + sliderEl.scrollLeft + (itemRect.width / 2);

    const targetLeft = itemCenterWithinSliderScroll - (sliderEl.clientWidth / 2);
    const maxLeft = Math.max(0, sliderEl.scrollWidth - sliderEl.clientWidth);

    sliderEl.scrollTo({ left: clamp(targetLeft, 0, maxLeft), behavior: 'auto' });
  }

  class ReviewsDotsSync {
    constructor(section) {
      this.section = section;
      this.slider = section.querySelector(SELECTORS.slider);
      this.items = Array.from(section.querySelectorAll(SELECTORS.item));
      this.dots = Array.from(section.querySelectorAll(SELECTORS.dot));

      if (!this.slider || !this.items.length || !this.dots.length) return;

      this.enabled = false;
      this.activeIndex = 0;

      this.rafId = 0;
      this.resizeRaf = 0;

      this.lastViewportWidth = window.innerWidth;
      this.lastSliderWidth = this.slider.clientWidth;

      this.onScroll = this.onScroll.bind(this);
      this.onResize = this.onResize.bind(this);
      this.syncNow = this.syncNow.bind(this);
    }

    enable() {
      if (this.enabled) return;
      this.enabled = true;

      this.activeIndex = 0;
      if (this.slider.scrollLeft !== 0) {
        this.slider.scrollTo({ left: 0, behavior: 'auto' });
      }
      setActiveDot(this.dots, 0);

      this.lastViewportWidth = window.innerWidth;
      this.lastSliderWidth = this.slider.clientWidth;

      this.slider.addEventListener('scroll', this.onScroll, { passive: true });

      window.addEventListener('resize', this.onResize, { passive: true });
      window.addEventListener('orientationchange', this.onResize, { passive: true });

      requestAnimationFrame(this.syncNow);
    }

    disable() {
      if (!this.enabled) return;
      this.enabled = false;

      this.slider.removeEventListener('scroll', this.onScroll);
      window.removeEventListener('resize', this.onResize);
      window.removeEventListener('orientationchange', this.onResize);

      if (this.rafId) cancelAnimationFrame(this.rafId);
      if (this.resizeRaf) cancelAnimationFrame(this.resizeRaf);
      this.rafId = 0;
      this.resizeRaf = 0;

      this.activeIndex = 0;
      setActiveDot(this.dots, 0);
    }

    onScroll() {
      if (this.rafId) return;
      this.rafId = requestAnimationFrame(() => {
        this.rafId = 0;
        this.syncNow();
      });
    }

    syncNow() {
      if (!this.enabled) return;

      const nextIdx = getClosestIndexToViewportCenter(this.slider, this.items);

      if (nextIdx !== this.activeIndex) {
        this.activeIndex = nextIdx;
        setActiveDot(this.dots, nextIdx);
      }
    }

    onResize() {
      if (!this.enabled) return;

      if (this.resizeRaf) return;
      this.resizeRaf = requestAnimationFrame(() => {
        this.resizeRaf = 0;

        const vw = window.innerWidth;
        const sw = this.slider.clientWidth;

        const viewportWidthChanged = vw !== this.lastViewportWidth;
        const sliderWidthChanged = sw !== this.lastSliderWidth;

        this.lastViewportWidth = vw;
        this.lastSliderWidth = sw;

        if (!viewportWidthChanged && !sliderWidthChanged) return;

        this.syncNow();
        const idx = clamp(this.activeIndex, 0, this.items.length - 1);
        centerItemInSlider(this.slider, this.items[idx]);
      });
    }
  }

  function init() {
    const sections = Array.from(document.querySelectorAll(SELECTORS.section));
    const instances = sections
      .map((s) => new ReviewsDotsSync(s))
      .filter(Boolean);

    const apply = () => {
      instances.forEach((inst) => {
        if (MOBILE_MQ.matches) inst.enable();
        else inst.disable();
      });
    };

    apply();
    MOBILE_MQ.addEventListener?.('change', apply);
    MOBILE_MQ.addListener?.(apply);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();